// client/src/components/mischief/MarkerArrow.js
//
// Standalone hand-drawn arrow. Unlike MarkerCircle/Underline which
// wrap content, arrows are positioned freely and point AT things.
//
// Each (variant × direction) is its own hand-authored path — no
// programmatic rotation. That's because an arrowhead rotated 180°
// looks mechanical; a hand-drawn "leftward" arrow has a different
// stroke rhythm than a hand-drawn "rightward" arrow, and honoring
// that is what makes the whole design system feel drawn.
//
// Usage:
//   <MarkerArrow direction="left" />
//   <MarkerArrow direction="down" color="red" />
//   <MarkerArrow variant="curly" direction="right" size="lg" />
//   <MarkerArrow className="absolute top-4 -right-8" direction="right" />

const COLOR_CLASS = {
  marker: 'text-marker',
  red:    'text-red',
  accent: 'text-accent',
};

// Fixed pixel sizes. Unlike Sticker's fontsize tokens, arrows don't
// participate in a type scale — they're objects, not text.
const SIZE_PX = {
  sm: 24,
  md: 40,
  lg: 64,
};

// Path library, keyed by variant then direction.
// All paths are authored in a 100x100 viewBox.
// - "pointing" variant: single graceful curve to an arrowhead
// - "curly" variant: tail loops back on itself before extending
//
// Each direction is authored so the arrowhead lands at the natural
// edge (left arrow ends at the left, up arrow ends at the top, etc.)
// and the tail starts opposite. Small overshoots and asymmetries
// throughout — that's the wobble.
const PATHS = {
  pointing: {
    // Shaft curves in from the right; arrowhead points left.
    left: [
      'M 90 50 C 70 45, 50 55, 30 50 C 20 47, 15 48, 10 50', // shaft
      'M 10 50 L 22 42',                                     // upper head
      'M 10 50 L 22 58',                                     // lower head
    ],
    // Shaft curves in from the left; arrowhead points right.
    right: [
      'M 10 50 C 30 55, 50 45, 70 50 C 80 53, 85 52, 90 50',
      'M 90 50 L 78 42',
      'M 90 50 L 78 58',
    ],
    // Shaft curves down from the top; arrowhead points up.
    up: [
      'M 50 90 C 45 70, 55 50, 50 30 C 48 20, 49 15, 50 10',
      'M 50 10 L 42 22',
      'M 50 10 L 58 22',
    ],
    // Shaft curves up from the bottom; arrowhead points down.
    down: [
      'M 50 10 C 55 30, 45 50, 50 70 C 52 80, 51 85, 50 90',
      'M 50 90 L 42 78',
      'M 50 90 L 58 78',
    ],
  },
  curly: {
    // Tail loops in the upper-right, then extends left with head.
    left: [
      'M 88 30 C 96 25, 96 40, 85 42 C 74 44, 65 40, 55 48 C 40 58, 25 52, 12 50',
      'M 12 50 L 24 42',
      'M 12 50 L 24 58',
    ],
    // Tail loops in the upper-left, then extends right with head.
    right: [
      'M 12 30 C 4 25, 4 40, 15 42 C 26 44, 35 40, 45 48 C 60 58, 75 52, 88 50',
      'M 88 50 L 76 42',
      'M 88 50 L 76 58',
    ],
    // Tail loops in the lower-right, then extends up with head.
    up: [
      'M 70 88 C 75 96, 60 96, 58 85 C 56 74, 60 65, 52 55 C 42 40, 48 25, 50 12',
      'M 50 12 L 42 24',
      'M 50 12 L 58 24',
    ],
    // Tail loops in the upper-right, then extends down with head.
    down: [
      'M 70 12 C 75 4, 60 4, 58 15 C 56 26, 60 35, 52 45 C 42 60, 48 75, 50 88',
      'M 50 88 L 42 76',
      'M 50 88 L 58 76',
    ],
  },
};

export function MarkerArrow({
  direction = 'left',
  variant = 'pointing',
  size = 'md',
  color = 'marker',
  strokeWidth = 2.5,
  className = '',
}) {
  // Resolve the path list. Fall back to pointing/left if someone
  // passes a bad prop — better than crashing.
  const paths =
    (PATHS[variant] ?? PATHS.pointing)[direction] ??
    PATHS.pointing.left;

  const px = SIZE_PX[size] ?? SIZE_PX.md;
  const colorClass = COLOR_CLASS[color] ?? COLOR_CLASS.marker;

  return (
    <svg
      aria-hidden="true"
      className={`${colorClass} ${className}`}
      width={px}
      height={px}
      viewBox="0 0 100 100"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
}